import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { green } from "@material-ui/core/colors";
import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography
} from '@material-ui/core';

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import QueueOverrideAuthorizationModal from "../QueueOverrideAuthorizationModal";
import toastError from "../../errors/toastError";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";
import { toast } from "react-toastify";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

const useStyles = makeStyles(theme => ({
  maxWidth: {
    width: "100%",
    padding: "0 0 20px 0",
  },

  dialogContent: {
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

	floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  queueOverrideCard: {
    borderRadius: "20px",
    padding: "10px",
    marginBottom: "5px",
  },

  queueOverrideCardActionArea: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    borderRadius: "20px",
    padding: "5px",
  },

  queueOverrideText: {
    fontSize: "16px",
  },

  queueOverrideSubText: {
    fontSize: "13px",
    color: "#959EAC",
  },

  buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

  chip: {
		margin: 2,
    color: theme.palette.text.primary,
	},
  
  chip_pending: {
    border: `2px solid #ffc107`,
  },

  chip_approved: {
    border: `2px solid #4caf50`,
  },

  chip_rejected: {
    border: `2px solid #e91e63`,
  },

  chip_expired: {
    border: `2px solid #9c27b0`,
  },
}));

const QueueOverrideListModal = ({ open, onClose }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();

  const {
    queueOverrides
    , queueOverridesDispatch
    , setAreThereQueueOverrideRequests
    , setShouldSyncRouteOnMenu
  } = useGlobalRegisters();
  
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedQueueOverride, setSelectedQueueOverride] = useState(null);

  const [queueOverrideAuthorizationModalOpen, setQueueOverrideAuthorizationModalOpen] = useState(false);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;

    try {
      setLoading(true);

      const delayDebounceFN = setTimeout(() => {
        const fetchQueueOverrides = async () => {
          const { data } = await api.get("/queueOverrides", { params: { pageNumber } });
          queueOverridesDispatch({ type: "LOAD_QUEUE_OVERRIDES", payload: data.queueOverrides });
          setHasMore(data.hasMore);
        };

        fetchQueueOverrides();
      }, 500);

      return () => clearTimeout(delayDebounceFN);
    }
    catch (exception) {
      console.log("queueOverrideListModal useEffect exception:", exception);
      toastError(exception);
    }
    finally {
      setLoading(false);
    }
  }, [open, pageNumber, queueOverridesDispatch]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleResetStates = () => {
    setLoading(false);
    setHasMore(false);
    setPageNumber(1);
    setSelectedQueueOverride(null);
    setQueueOverrideAuthorizationModalOpen(false);
    queueOverridesDispatch({ type: "RESET" });
  };

  const handleClose = () => {
    onClose();
    handleResetStates();
  };

  const handleLoadQueueOverrides = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/queueOverrides", { params: { pageNumber } });
      queueOverridesDispatch({ type: "LOAD_QUEUE_OVERRIDES", payload: data.queueOverrides });
      setHasMore(data.hasMore);

      if (data.queueOverrides.length === 0) {
        onClose();
        setAreThereQueueOverrideRequests(false);
      }
    }
    catch (exception) {
      console.log("handleLoadQueueOverrides exception:", exception);
      toastError(exception);
    }
    finally {
      setLoading(false);
    }
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleOpenQueueOverrideAuthorizationModal = (queueOverride) => {
    if (!user.canAuthorizeQueueOverride) return;

    setSelectedQueueOverride(queueOverride);
    setQueueOverrideAuthorizationModalOpen(true);
  };

  const handleCloseQueueOverrideAuthorizationModal = () => {
    setSelectedQueueOverride(null);
    setQueueOverrideAuthorizationModalOpen(false);
  };

  const handleQueueOverrideCardClick = async (queueOverride) => {
    if (!["pending", "approved"].includes(queueOverride.status)) return;

    if (queueOverride.status === "pending" && user.canAuthorizeQueueOverride) {
      handleOpenQueueOverrideAuthorizationModal(queueOverride);
      return;
    }
    
    if (queueOverride.status === "approved") {
      try {
        const { data: { canUserAccessTicket } } = await api.post(`/tickets/${queueOverride.ticketId}/validateTicketAccess`);
        
        if (!canUserAccessTicket) {
          toast.info(i18n.t("queueOverrideListModal.toasts.noAccessToTicket"));
          return;
        }

        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${queueOverride.ticketId}`);
        setShouldSyncRouteOnMenu(true);
        handleClose();
      }
      catch (exception) {
        console.log("- handleQueueOverrideCardClick exception:", exception);
        toastError(exception);
      }
    }
  };

  const renderQueueOverrideCard = (queueOverride) => {
    const shouldDisableQueueOverrideInteraction = !user.canAuthorizeQueueOverride && queueOverride.status !== "approved";

    return (
      <Card
        id={`card-${queueOverride.id}`}
        className={classes.queueOverrideCard}
        onClick={async () => await handleQueueOverrideCardClick(queueOverride)}
      >
        <CardActionArea
          className={classes.queueOverrideCardActionArea}
          style={{ cursor: shouldDisableQueueOverrideInteraction ? "default" : "pointer" }}
          disabled={shouldDisableQueueOverrideInteraction}
        >
          <Avatar alt={queueOverride.contact.name} src={queueOverride.contact.profilePicUrl} />

          <CardContent>
            <Typography className={classes.queueOverrideText} gutterBottom component="h3">
              {queueOverride.contact.name}
            </Typography>

            <Typography className={classes.queueOverrideSubText} gutterBottom component="h3">
              {i18n.t("queueOverrideListModal.card.user")} {queueOverride.requestUser.name}
            </Typography>

            <Chip
              variant="outlined"
              className={`${classes.chip} ${classes[`chip_${queueOverride.status}`]}`}
              label={i18n.t(`queueOverrideListModal.card.status.${queueOverride.status}`)}
            />
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      {user.canAuthorizeQueueOverride && (
        <QueueOverrideAuthorizationModal
          open={queueOverrideAuthorizationModalOpen}
          onClose={handleCloseQueueOverrideAuthorizationModal}
          queueOverride={selectedQueueOverride}
          handleResetStates={handleResetStates}
          handleLoadQueueOverrides={handleLoadQueueOverrides}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        scroll="paper"
      >
        <DialogTitle>
          {i18n.t("queueOverrideListModal.title")}
        </DialogTitle>

        <DialogContent
          className={classes.dialogContent}
          onScroll={handleScroll}
          dividers
        >
          {!loading && queueOverrides.map(queueOverride => renderQueueOverrideCard(queueOverride))}

          {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </DialogContent>

        <DialogActions>
          <Button
            color="inherit"
            variant="outlined"
            className={classes.floatingButton}
            onClick={handleClose}
          >
            {i18n.t("queueOverrideListModal.buttons.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QueueOverrideListModal;
